/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { TranslationFeedback_ReasonForFeedback as ReasonForFeedback } from '__generated__/graphql-types';

import { Button, Checkbox, CheckboxGroup, Grid, TextField, Typography2 } from '@coursera/cds-core';
import { ThumbsDownFilledIcon, ThumbsUpFilledIcon } from '@coursera/cds-icons';

import { useGiveTranslationFeedback } from 'bundles/composable-page-header/legacy/translation/useGiveTranslationFeedback';
import type { Feedback, SelectFeedbackTypeContentProps } from 'bundles/composable-page-header/types';

import _t from 'i18n!nls/composable-page-header';

const styles = {
  container: css`
    padding-top: var(--cds-spacing-200);
  `,
};

type Props = {
  onClose: () => void;
};

const SelectFeedbackTypeContent = ({ onGoodFeedback, onBadFeedback }: SelectFeedbackTypeContentProps) => {
  return (
    <Grid container spacing={24} xs={12}>
      <Grid item xs={12}>
        <Typography2 variant="titleSmall" component="h3">
          {_t('Is this translation accurate?')}
        </Typography2>
      </Grid>
      <Grid item xs={12}>
        <Typography2 component="p" variant="bodyPrimary">
          {_t('Your feedback will be used to improve this translation for other learners.')}
        </Typography2>
      </Grid>
      <Grid item container xs={12} spacing={16}>
        <Grid item xs="auto">
          <Button variant="secondary" onClick={onGoodFeedback} icon={<ThumbsUpFilledIcon />}>
            {_t('Yes')}
          </Button>
        </Grid>
        <Grid item xs="auto">
          <Button variant="secondary" onClick={onBadFeedback} icon={<ThumbsDownFilledIcon />}>
            {_t('No')}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

type AddFeedbackDetailsContentProps = {
  onGiveFeedback: (feedback: string, reasonsForFeedback: ReasonForFeedback[]) => void;
  onSkipFeedback: () => void;
};

const AddFeedbackDetailsContent = ({ onGiveFeedback, onSkipFeedback }: AddFeedbackDetailsContentProps) => {
  const [moreInfo, setMoreInfo] = useState('');
  const [reasonForFeedback, setReasonForFeedback] = useState<ReasonForFeedback[]>([]);

  return (
    <Grid container spacing={32} xs={12}>
      <Grid item xs={12}>
        <Typography2 variant="titleSmall" component="h3">
          {_t('Add translation feedback')}
        </Typography2>
      </Grid>
      <Grid item xs={12}>
        <CheckboxGroup
          label={_t("What's wrong with this translation?")}
          value={reasonForFeedback}
          onChange={(event) => {
            const value = event.target.value;
            if (reasonForFeedback.includes(value)) {
              setReasonForFeedback(reasonForFeedback.filter((v) => v !== value));
            } else if (Object.values(ReasonForFeedback).includes(value)) {
              const reason = value as ReasonForFeedback;
              setReasonForFeedback([...reasonForFeedback, reason]);
            }
          }}
        >
          <Checkbox label={_t('It’s harmful or offensive')} value={ReasonForFeedback.HarmfulOffensive} />
          <Checkbox label={_t('It’s hard to understand')} value={ReasonForFeedback.HardToUnderstand} />
          <Checkbox label={_t('Some parts of it aren’t translated')} value={ReasonForFeedback.PartialTranslation} />
          <Checkbox label={_t('Something else')} value={ReasonForFeedback.Other} />
        </CheckboxGroup>
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label={_t('How could it be improved?')}
          placeholder={_t('Enter the text that has a problem or suggest a better translation.')}
          value={moreInfo}
          onChange={(e) => setMoreInfo(e.target.value)}
        />
      </Grid>
      <Grid item container xs={12} spacing={16}>
        <Grid item xs="auto">
          <Button
            disabled={moreInfo.length === 0 || reasonForFeedback.length === 0}
            size="medium"
            variant="primary"
            onClick={() => {
              onGiveFeedback(moreInfo, reasonForFeedback);
            }}
          >
            {_t('Submit')}
          </Button>
        </Grid>
        <Grid item xs="auto">
          <Button
            size="medium"
            variant="secondary"
            onClick={() => {
              onSkipFeedback();
            }}
          >
            {_t('Skip')}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

const LanguageTranslationFeedbackSubMenu = ({ onClose }: Props) => {
  const giveFeedback = useGiveTranslationFeedback();
  const [feedback, setFeedback] = useState<Feedback>('');

  return (
    <div css={styles.container}>
      {!feedback && (
        <SelectFeedbackTypeContent
          onGoodFeedback={() => {
            onClose();
          }}
          onBadFeedback={() => {
            setFeedback('No');
          }}
        />
      )}
      {feedback === 'No' && (
        <AddFeedbackDetailsContent
          onGiveFeedback={(detailedFeedback, reasonsForFeedback) => {
            giveFeedback(detailedFeedback, reasonsForFeedback);
            onClose();
          }}
          onSkipFeedback={() => {
            onClose();
          }}
        />
      )}
    </div>
  );
};

export default LanguageTranslationFeedbackSubMenu;
